import React, { useState, useCallback, useEffect, useRef } from "react";
import { AutoComplete, Spin, Tag } from "antd";
import { WebSocket as MockWebSocket } from "mock-socket";
import "./AutoComplete.scss";
import { useMercuryContext } from "../../context";

interface IOption {
  value: string;
  title: string;
  url: string;
  _id: string;
  /** Result kind from the cross-collection typeahead: "project" | "plp" | "document". */
  kind?: string;
}

/** Pill label and colour per result kind (unknown kinds fall back to the raw kind, capitalised). */
const KIND_PILL: Record<string, { label: string; color: string }> = {
  project: { label: "Project", color: "blue" },
  plp: { label: "PLP", color: "green" },
  document: { label: "Document", color: "purple" },
};

/** Group headers in display order; results of any other kind go last under their raw kind. */
const KIND_GROUPS: Record<string, string> = { project: "Projects", plp: "PLPs", document: "Documents" };

/** Results per kind requested from the backend when no `limit` prop is given. */
const DEFAULT_LIMIT = 15;
/** Dropdown list height (px): the list scrolls beyond it instead of growing off the page. */
const LIST_HEIGHT = 420;

const kindPill = (kind?: string) => {
  if (!kind) return null;
  const pill = KIND_PILL[kind] ?? { label: kind.charAt(0).toUpperCase() + kind.slice(1), color: "default" };
  return (
    <Tag className="autocomplete-kind" color={pill.color}>
      {pill.label}
    </Tag>
  );
};

interface IForm {
  setFieldsValue: (data: any) => void;
}

interface IAutoCompleteComponentProps {
  form?: IForm;
  name?: string;
  websocket?: string;
  emit?: string;
  /** An explicit socket to use instead of the context's project socket. */
  socket?: any;
  handleMainMenuSelection?: (selection: string) => void;
  navigate?: (url: string) => void;
  /** Called with the selected option's value (and the option) on selection. */
  onValueSelected?: (value: string, option?: any) => void;
  /** Result kinds to search ("project" | "plp" | "document"); every kind when omitted. */
  kinds?: string[];
  /** Results PER KIND to request (default 15; the backend caps at 50). */
  limit?: number;
  /** Show the kind pill on each row (default true). */
  showPills?: boolean;
  /**
   * Enter behaviour. Default (false): the highlighted option — the top result
   * by default — is selected, and navigated to when `navigate` is given.
   * `true` (filter mode): Enter submits the current input text via `onSubmit`,
   * like a text filter; an option the user has explicitly highlighted is
   * selected first, so `onSubmit` receives that option's value instead.
   */
  submitText?: boolean;
  /** Called on Enter in `submitText` mode with the text (or highlighted option's value). */
  onSubmit?: (text: string) => void;
}

const AutoCompleteComponent: React.FC<IAutoCompleteComponentProps> = ({
  form = { setFieldsValue: () => {} },
  name = "linkproject",
  websocket,
  emit,
  socket: providedSocket,
  handleMainMenuSelection,
  navigate,
  onValueSelected,
  kinds,
  limit = DEFAULT_LIMIT,
  showPills = true,
  submitText = false,
  onSubmit
}) => {
  const context = useMercuryContext();
  /** The latest input/selection, readable after antd has finished handling the same keystroke. */
  const latestValue = useRef<string>("");
  /** Set when a selection navigated away, so a same-keystroke Enter does not also submit. */
  const navigated = useRef<boolean>(false);

  const [value, setValue] = useState<string>("");
  const [options, setOptions] = useState<IOption[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [loadingOptions, setLoadingOptions] = useState<boolean>(false);

  const onSearch = (data: string) => {
    if (data) {
      if (data.length > 1) {
        getOptionsData(data);
      } else {
        setOptions([]);
      }
      setValue(data);
      latestValue.current = data;
      form.setFieldsValue({ [name]: data });
    } else {
      setValue("");
      latestValue.current = "";
      setOptions([]);
      form.setFieldsValue({ [name]: "" });
    }
  };

  /**
   * Filter mode: Enter submits the input. Deferred a tick so that, if antd is
   * selecting a highlighted option on the same keystroke, `onSelect` has run
   * first and the submitted value is that option's.
   */
  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (!submitText || e.key !== "Enter") return;
    e.preventDefault();
    navigated.current = false;
    setTimeout(() => {
      if (navigated.current) return;
      const text = latestValue.current.trim();
      setIsDropdownOpen(false);
      onSubmit?.(text);
    }, 0);
  };

  const onSelect = (value: string, option: any) => {
    setValue(value);
    latestValue.current = value;
    onValueSelected?.(value, option);
    // Keyed by `name` so a form field (e.g. LinkProject's "linkproject") receives the selection.
    form.setFieldsValue({ [name]: value });
    setIsDropdownOpen(false);
    if (handleMainMenuSelection) {
      handleMainMenuSelection("projects");
    }
    if (navigate) {
      navigated.current = true;
      closeDropdown();
      navigate(option.url);
    }
  };

  useEffect(() => {
    let activeSocket: any;

    if (websocket) {
      activeSocket = new MockWebSocket(websocket);
    } else {
      activeSocket = providedSocket || context?.gpSocket?.project;
    }

    if (activeSocket && typeof activeSocket.on === "function") {
      activeSocket.on(emit, (data: any) => {
        setOptions((prevOptions) => [...prevOptions, data]);
      });

      return () => {
        if (typeof activeSocket.off === "function") {
          activeSocket.off(emit);
        }
      };
    } else {
      console.error("Socket is not initialized or does not have 'on' method");
    }
  }, [context, websocket, emit, providedSocket]);

  const closeDropdown = () => {
    setIsDropdownOpen(false);
    setLoadingOptions(false);
    setValue("");
    setOptions([]);
  };

  const getOptionsData = useCallback(
    async (search: string) => {
      setLoadingOptions(true);
      setOptions([]);
  
      const activeSocket = websocket ? new MockWebSocket(websocket) : providedSocket || context?.gpSocket?.project;
      const request = { search, limit, ...(kinds ? { kinds } : {}) };

      if (activeSocket && typeof activeSocket.emit === "function") {
        activeSocket.emit(emit, request, () => {
          setLoadingOptions(false);
        });
      } else {
        // Use mock web socket if set
        if (activeSocket.readyState === WebSocket.OPEN) {
          activeSocket.send(JSON.stringify(request));
        } else {
          activeSocket.onopen = () => {
            activeSocket.send(JSON.stringify(request));
          };
        }

        activeSocket.onmessage = (event: MessageEvent) => {
          const data = JSON.parse(event.data);
          setOptions(data);
          setLoadingOptions(false);
        };

        activeSocket.onerror = () => {
          console.error("WebSocket error occurred");
          setLoadingOptions(false);
        };
      }
    },
    [context, websocket, emit, providedSocket, kinds, limit]
  );

  const handleDropdownChange = (open: boolean) => {
    setIsDropdownOpen(open);
  };

  const toOption = (o: IOption) => ({
    key: o._id,
    value: o.value,
    url: o.url,
    title: o.value,
    label: (
      <span className="autocomplete-option">
        {showPills && kindPill(o.kind)}
        <span className="autocomplete-option-text">{o.value}</span>
      </span>
    ),
  });

  // Grouped by kind (in KIND_GROUPS order) when results carry one; a flat list otherwise.
  const groupedOptions = (() => {
    if (!options.some((o) => o.kind)) return options.map(toOption);
    const order = [...Object.keys(KIND_GROUPS), ...options.map((o) => o.kind ?? "").filter((k) => k && !(k in KIND_GROUPS))];
    return Array.from(new Set(order))
      .map((kind) => ({
        key: kind,
        label: KIND_GROUPS[kind] ?? kind.charAt(0).toUpperCase() + kind.slice(1),
        options: options.filter((o) => (o.kind ?? "") === kind).map(toOption),
      }))
      .filter((group) => group.options.length);
  })();

  return (
    <div className="search-project-autocomplete">
      {loadingOptions && <Spin size="small" style={{ marginRight: "10px" }} />}
      <AutoComplete
        style={{ minWidth: '150px' }}
        onSearch={onSearch}
        onSelect={onSelect}
        value={value}
        popupMatchSelectWidth={value.length > 1 ? 850 : 250}
        popupClassName="search-project-autocomplete-dropdown"
        listHeight={LIST_HEIGHT}
        options={groupedOptions}
        onDropdownVisibleChange={handleDropdownChange}
        open={isDropdownOpen}
        defaultActiveFirstOption={!submitText}
        onKeyDown={onKeyDown}
        allowClear
        onClear={closeDropdown}
        placeholder="Search project"
        notFoundContent={
          value.length <= 1
            ? "Please type at least 2 characters"
            : loadingOptions
            ? "Loading..."
            : "No Match"
        }
      />
    </div>
  );
};

export default AutoCompleteComponent;